import {
  useSalesServiceSalesInvoicesBrokerPayCreate,
  useSalesServiceSalesInvoicesSendPaymentLinkCreate,
  useSalesServiceSalesInvoicesStaffPayCreate,
  useSalesServiceSalesOrdersModificationPriceCreate,
  useSalesServiceSalesOrdersModifyCreate,
  useSalesServiceSalesProductsMedicalTravelOrderBrokerCreate,
  useSalesServiceSalesProductsMedicalTravelOrderStaffCreate,
  useSalesServiceSalesProductsPriceCreate
} from '@insurance/api/queries'
import {
  ContractorType,
  InvoiceDetail,
  OrderInspectionStatus,
  OrderProduct,
  PaymentTypeStaff
} from '@insurance/api/requests'
import {
  PayBrokerButton,
  PaymentExtendType,
  PaymentValue,
  PayStaffButton
} from '@insurance/components'
import { useAppNavigate, useModifyPayment } from '@insurance/hooks'
import { paths } from '@insurance/routes'
import { ProductCode } from '@insurance/types'
import { DEFAULT_CURRENCY } from '@shared/constants'
import { useSessionContext } from '@shared/hooks'
import {
  getFormatCurrency,
  replaceEmptyWithUndefined,
  showApiErrors
} from '@shared/utils'
import { App, Button, Flex, Form, Modal } from 'antd'
import { useTranslation } from 'react-i18next'

import { useHealthInsuranceValues } from './HealthInsuranceContext'
import {
  normalizeCommonData,
  normalizeModifyOrderData,
  normalizeOrderData,
  removeHealthValuesFromStorage
} from './utils'

const { confirm } = Modal

type Props = {
  steps: number
  onReadyToSaveData?: () => void
}

export const HealthInsuranceControls = ({
  steps,
  onReadyToSaveData
}: Props) => {
  const { t } = useTranslation()
  const formInstance = Form.useFormInstance()
  const { message } = App.useApp()
  const navigate = useAppNavigate()
  const { order, isEditable } = useHealthInsuranceValues()
  const { broker } = useSessionContext()

  const step = Form.useWatch('step', { preserve: true })
  const orderPrice = Form.useWatch('order_price', { preserve: true })
  const price = Form.useWatch('price', { preserve: true })
  const contractor = Form.useWatch('contractor', { preserve: true })

  const isLegalEntity = contractor?.content_type === ContractorType.LEGALENTITY

  const hasPrevStep = step > 0
  const isCalculateStep = step === 0
  const isPreparePersons = step === 1
  const isPayStep = step === steps - 1

  const handleChangeStep = (step: number) => {
    formInstance.setFieldValue('step', step)
    onReadyToSaveData?.()
  }

  const handleFinishStepError = (error: unknown) => {
    showApiErrors(error, formInstance)
  }

  const handleSuccessCommonPayment = () => {
    removeHealthValuesFromStorage(order?.id)
    message.success(
      t(
        isEditable
          ? 'insurance_modified_successfully'
          : 'insurance_created_successfully'
      )
    )
  }

  const { mutate: calculate, isPending: isCalculatingLoading } =
    useSalesServiceSalesProductsPriceCreate({
      onError: handleFinishStepError
    })

  const { mutate: payBroker, isPending: isBrokerPayLoading } =
    useSalesServiceSalesInvoicesBrokerPayCreate({
      onSuccess: ({ invoice }) => {
        handleSuccessCommonPayment()
        navigate(
          invoice?.order.id
            ? paths.orders.details(invoice.order.id)
            : paths.salesPanel.root
        )
      },
      onError: handleFinishStepError
    })

  const { mutate: pay, isPending: isPayLoading } =
    useSalesServiceSalesInvoicesStaffPayCreate({
      onSuccess: ({ invoice }) => {
        handleSuccessCommonPayment()
        navigate(
          invoice?.order.id
            ? paths.orders.details(invoice.order.id)
            : paths.salesPanel.root
        )
      },
      onError: (error, variables) => {
        handleFinishStepError(error)

        const isPayCash =
          variables?.requestBody?.payment_type === PaymentTypeStaff.CASH

        const errorStatus = (error as { status?: number })?.status

        if (isPayCash && errorStatus !== 500) {
          confirm({
            title: t('error_payment'),
            content: t('error_payment_confirmation'),
            okText: t('pay_manually'),
            onOk: () => {
              pay({
                id: variables.id,
                requestBody: {
                  ...variables.requestBody,
                  force_manual: true
                }
              })
            }
          })
        }
      }
    })

  const { mutate: paymentLink, isPending: isPaymentLinkLoading } =
    useSalesServiceSalesInvoicesSendPaymentLinkCreate({
      onSuccess: handleSuccessCommonPayment,
      onError: handleFinishStepError
    })

  const { mutate: initBrokerOrder, isPending: isInitBrokerOrderLoading } =
    useSalesServiceSalesProductsMedicalTravelOrderBrokerCreate({
      onError: handleFinishStepError
    })

  const { mutate: initStaffOrder, isPending: isInitStaffOrderLoading } =
    useSalesServiceSalesProductsMedicalTravelOrderStaffCreate({
      onError: handleFinishStepError
    })

  const { mutate: initModifyOrder, isPending: isInitModifyOrderLoading } =
    useSalesServiceSalesOrdersModifyCreate({
      onError: handleFinishStepError
    })

  const { mutate: calculateModify, isPending: isCalculatingModifyLoading } =
    useSalesServiceSalesOrdersModificationPriceCreate({
      onSuccess: (data) => {
        handleChangeStep(step + 1)

        formInstance.setFieldValue('price', {
          currency: DEFAULT_CURRENCY,
          amount: Number(Number(data?.price_data?.amount)?.toFixed(2))
        })
        window.scrollTo({ top: 0, behavior: 'smooth' })
      },
      onError: handleFinishStepError
    })

  const { onFinishPay, payPermission, isPaymentLoading } = useModifyPayment({
    formInstance
  })

  const handleCalculate = async () => {
    await formInstance.validateFields()
    const values = formInstance.getFieldsValue(true)

    if (order?.id)
      calculateModify({
        id: order.id,
        requestBody: normalizeModifyOrderData(values)
      })
    else
      calculate(
        {
          code: ProductCode.MEDICAL_TRAVEL,
          requestBody: normalizeCommonData(values)
        },
        {
          onSuccess: (data) => {
            handleChangeStep(step + 1)

            formInstance.setFieldValue('price', {
              currency: DEFAULT_CURRENCY,
              amount: Number(Number(data?.amount)?.toFixed(2))
            })
            window.scrollTo({ top: 0, behavior: 'smooth' })
          }
        }
      )
  }

  const handleGoToCustomer = async () => {
    await formInstance.validateFields()
    const values = formInstance.getFieldsValue(true)

    if (order?.id)
      calculateModify({
        id: order.id,
        requestBody: normalizeModifyOrderData(values)
      })
    else
      calculate(
        {
          code: ProductCode.MEDICAL_TRAVEL,
          requestBody: normalizeCommonData(values, false, true)
        },
        {
          onSuccess: (data) => {
            handleChangeStep(step + 1)

            formInstance.setFieldValue('price', {
              currency: DEFAULT_CURRENCY,
              amount: Number(Number(data?.amount)?.toFixed(2))
            })
            window.scrollTo({ top: 0, behavior: 'smooth' })
          }
        }
      )
  }

  const handlePrevStep = () => {
    const prevStep = step - 1

    handleChangeStep(prevStep)

    if (!prevStep) formInstance.setFieldValue('price', undefined)
  }

  const handleInitBrokerOrder = async () => {
    await formInstance.validateFields()
    const values = formInstance.getFieldsValue(true)

    const handleSuccess = ({ invoice }: OrderProduct) => {
      if (!invoice?.id) return

      payBroker({
        id: invoice.id
      })
    }

    initBrokerOrder(
      { requestBody: replaceEmptyWithUndefined(normalizeOrderData(values)) },
      {
        onSuccess: handleSuccess
      }
    )
  }

  const handleInitStaffOrder = async (paymentValue: PaymentValue) => {
    await formInstance.validateFields()
    const values = formInstance.getFieldsValue(true)

    const handleSuccess = (invoice: InvoiceDetail | null) => {
      if (!invoice?.id) return

      if (paymentValue.paymentType !== PaymentExtendType.ONLINE_PAYMENT)
        pay({
          id: invoice.id,
          requestBody: {
            payment_type: paymentValue.paymentType,
            cash_box: paymentValue.cashBox
          }
        })

      if (paymentValue.paymentType === PaymentExtendType.ONLINE_PAYMENT)
        paymentLink(
          { id: invoice.id },
          {
            onSuccess: () => {
              navigate(
                invoice.order.id
                  ? paths.orders.details(invoice.order.id)
                  : paths.salesPanel.root
              )
            }
          }
        )
    }

    if (order?.id)
      initModifyOrder(
        {
          id: order.id,
          requestBody: replaceEmptyWithUndefined({
            ...normalizeModifyOrderData(values),
            status: OrderInspectionStatus.OFFER_READY
          })
        },
        {
          onSuccess: (order) => onFinishPay(order.id!, handleSuccess)
        }
      )
    else
      initStaffOrder(
        { requestBody: replaceEmptyWithUndefined(normalizeOrderData(values)) },
        {
          onSuccess: ({ invoice }) => handleSuccess(invoice)
        }
      )
  }

  const canPay =
    !isEditable || (payPermission && price?.amount > orderPrice?.amount)

  return (
    <Flex justify="end" gap={12}>
      {hasPrevStep && (
        <Button color="primary" variant="outlined" onClick={handlePrevStep}>
          {t('prev_step')}
        </Button>
      )}

      {isCalculateStep && (
        <Button
          type="primary"
          loading={isCalculatingLoading || isCalculatingModifyLoading}
          onClick={handleCalculate}
        >
          {t('calculate')}
        </Button>
      )}

      {isPreparePersons && (
        <Button
          disabled={!canPay}
          type="primary"
          loading={isCalculatingLoading}
          onClick={handleGoToCustomer}
        >
          {t('next_step')}
        </Button>
      )}

      {isPayStep && !broker && (
        <PayStaffButton
          price={getFormatCurrency({ amount: price?.amount })}
          disabled={!canPay}
          loading={
            isInitStaffOrderLoading ||
            isPayLoading ||
            isInitModifyOrderLoading ||
            isPaymentLinkLoading ||
            isPaymentLoading
          }
          isLegalEntity={isLegalEntity}
          onPay={handleInitStaffOrder}
        />
      )}

      {isPayStep && broker && (
        <PayBrokerButton
          disabledPay={!canPay}
          loading={isInitBrokerOrderLoading || isBrokerPayLoading}
          onPay={handleInitBrokerOrder}
        />
      )}
    </Flex>
  )
}
